import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SvgIconsComponent } from '@zayan-erp/shared';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { CommonService } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';
import { DatashareService } from '@zayan-erp/shared';
import { EMPTY, forkJoin, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../../../../apps/cinari-erp-client/src/environments/environment';
import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { mediaFiles } from '@zayan-erp/file-manager';
import { NgbModalService } from '@zayan-erp/shared';
import { NgbModal, NgbPopoverModule, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { FileDropComponent } from '../file-drop/file-drop.component';
import { SpinnerService } from '@zayan-erp/shared';
import { FileUploadService } from '../../services/file-upload.service';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    SvgIconsComponent,
    NgxFileDropModule,
    CommonModule,
    FormsModule,
    NgbPopoverModule,
    FileDropComponent,
    NgbTooltip
  ],
  templateUrl: './file-upload.component.html'
})
export class FileUploadComponent implements OnInit, OnDestroy {
  @ViewChild('uploadModal') uploadModal!: ElementRef;
  @Input('dataSource') dataSource!: any;

  private ngUnsubscribe = new Subject<void>();
  uploadingPercentage: any = 0;
  progressbar: string = '';
  public attachedFiles: mediaFiles[] = [];
  fileAcceptedFormat: string = environment.fileFormat;
  isUploadSubmitted: boolean = false;
  fileUploadCount: number = 0;
  isShowDuplicateMessage: boolean = false;
  replaceExisting: boolean = false;

  #fileUploadService = inject(FileUploadService);

  constructor(
    private _commonService: CommonService,
    private _datashare: DatashareService,
    private _toaster: ToastrService,
    private _ngbModalService: NgbModalService,
    private _modalService: NgbModal,
    private _spinnerService: SpinnerService
  ) {
    this._datashare.$updateFolderInformation.pipe(takeUntil(this.ngUnsubscribe)).subscribe(files => {
      if (files !== undefined || files !== null) {
        this.dataSource = JSON.parse(files);
      }
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  uploadFilesPreview(): void {
    this.replaceExisting = false;
    this.isUploadSubmitted = false;
    this._modalService.open(this.uploadModal, this._ngbModalService.centerModal);
  }

  getFileExtension(file: any): string {
    return `${file?.name.split('.').pop().toLowerCase()}`;
  }

  dropped(files: NgxFileDropEntry[]): void {
    this.uploadingPercentage = 0;
    for (const droppedFile of files) {
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        if (!this.isShowDuplicateMessage) {
          this.isShowDuplicateMessage =
            this.dataSource.filter(
              (x: any) => x.name.trim() === file.name.trim() && x.name.trim() === droppedFile.relativePath.trim()
            ).length > 0
              ? true
              : false;
        }
        if (this._commonService.isValidFileFormat(file)) {
          // if file with same name and relative path is already in the attachedFiles array, then don't add it again
          if (this.attachedFiles.some(f => f.name === file.name && f.relativePath === droppedFile.relativePath)) {
            this._toaster.error(
              `File \''${file.name}\'' with this name and relative path has already been added. Please rename the file or remove the existing one before uploading.`,
              'Error',
              {
                closeButton: true,
                disableTimeOut: true
              }
            );
            return;
          }
          this.attachedFiles.push({
            name: file.name,
            size: file.size,
            formattedSize: this._commonService.formatFileSize(file.size),
            isDuplicated:
              this.dataSource.filter(
                (x: any) => x.name.trim() === file.name.trim() && x.name.trim() === droppedFile.relativePath.trim()
              ).length > 0
                ? true
                : false,
            type: file.type,
            file: file,
            note: '',
            progress: 0,
            isUploadStarted: false,
            ngUnsubscribe: new Subject<any>(),
            relativePath: droppedFile.relativePath
          });
        } else {
          this._toaster.error('Invalid file format');
        }
      });
    }
  }

  backgroundColor(progress: number): string {
    if (progress <= 24) {
      this.progressbar = 'var(--bs-yellow)';
    } else if (progress >= 25 && progress < 49) {
      this.progressbar = 'var(--bs-orange)';
    } else if (progress >= 50 && progress <= 99) {
      this.progressbar = 'var(--bs-primary)';
    } else if (progress == 100) {
      this.progressbar = 'var(--bs-success)';
    }
    return this.progressbar;
  }

  fileSize(size: number): string {
    return this._commonService.formatFileSize(size);
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);
    this.isShowDuplicateMessage = false;
    this.attachedFiles.forEach(element => {
      this.isShowDuplicateMessage =
        this.dataSource.filter(
          (x: any) => x.name.trim() === element.name.trim() && x.name.trim() === element.relativePath.trim()
        ).length > 0
          ? true
          : false;
    });
  }

  uploadFiles(): void {
    this.isUploadSubmitted = true;
    this.fileUploadCount = 0;
    if (this.attachedFiles.length === 0) {
      this._toaster.error('No file is attached');
      return;
    }

    let Observable: Array<Observable<any>> = [];
    //this._spinnerService.handleSpinnerStart();
    for (let i = 0; i < this.attachedFiles.length; i++) {
      if (this.attachedFiles[i].note.trim() == '') {
        return;
      }
      this.attachedFiles[i].isUploadStarted = false;
      let formData = new FormData();
      formData.append(`${i}.file`, this.attachedFiles[i].file);
      formData.append(`${i}.notes`, this.attachedFiles[i].note);
      this._spinnerService.handleSpinnerStart();
      Observable.push(this.getSignedUrl(this.attachedFiles[i]));
    }
    forkJoin(Observable)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this._modalService.dismissAll();
          this._toaster.success('Files uploaded successfully', 'Success!');
          this.isShowDuplicateMessage = false;
          this.replaceExisting = false;
          this.isUploadSubmitted = false;
          this.attachedFiles = [];
          this.fileUploadCount = 0;
          this._datashare.updateFolderFiles(true);
        },

        complete: () => {
          this._toaster.success('Files uploaded successfully', 'Success!');
        },
        error: error => {
          this._toaster.error('Internal server error occured while processing your request');
        }
      });
  }

  private trackEventProgress(event: any, file: any, response: any): any {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        file.isUploadStarted = true;
        const percentDone = Math.round((100 * event.loaded) / event.total);
        // if (percentDone === 100) {
        //   // setTimeout(() => {
        //   //   file.progress = percentDone;
        //   // }, 2000);

        // } else {
        //   file.progress = percentDone;
        // }
        file.progress = percentDone;
        return null;
      case HttpEventType.Response:
        return response;
      default:
        return null;
    }
  }

  getSignedUrl(file: mediaFiles): Observable<{
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: mediaFiles;
  }> {
    return this.#fileUploadService.getPresignedUploadUrl(file.name).pipe(
      map((data: any) => ({
        fileName: data.fileName,
        presignedUrl: data.presignedUrl,
        s3Key: data.s3Key,
        sourceFile: file
      })),
      switchMap(fileObject => this.uploadFileToS3(fileObject)),
      switchMap(fileObject => this.notifyServerOfFileUpload(fileObject))
    );
  }

  uploadFileToS3(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: mediaFiles;
  }): Observable<{
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: mediaFiles;
  }> {
    let file: File = fileObject.sourceFile.file;

    return this.#fileUploadService
      .uploadToS3(fileObject.presignedUrl, file)
      .pipe(map(event => this.trackEventProgress(event, fileObject.sourceFile, fileObject)));
  }

  notifyServerOfFileUpload(
    fileObject: {
      fileName: string;
      presignedUrl: string;
      s3Key: string;
      sourceFile: mediaFiles;
    } | null
  ): Observable<{
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: mediaFiles;
  }> {
    if (fileObject === null) {
      return EMPTY;
    } else {
      const parentId = this._datashare.getFolderId() == '0' ? null : this._datashare.getFolderId();
      const uploadDetails = {
        fileName: fileObject.fileName,
        s3Key: fileObject.s3Key,
        notes: fileObject.sourceFile.note,
        folderId: parentId,
        relativePath:
          fileObject.sourceFile.relativePath === fileObject.fileName ? '' : fileObject.sourceFile.relativePath
      };

      return this.#fileUploadService.completePresignedUpload(uploadDetails, this.replaceExisting).pipe(
        map(() => {
          this._spinnerService.handleSpinnerStop();
          return fileObject;
        })
      );
    }
  }
}
